import { normalize, arrayOf, Schema } from 'normalizr';
import { denormalize } from 'denormalizr';

import { actionize, apiFetch, apiAction, entityReducer, stringParams } from 'utils';

/**
 * Provides a set of action creators, reducers, and helper functions to manage a data entity that is queried via
 * a REST API.
 *
 * The entities are stored normalized in redux state along with status flags that reflect its loading status.
 * For example the state shape for the "mexicanFoods" duck entity would look like this:
 *
 * ```
 * {
 *   mexicanFoods: {
 *     _all: {
 *       results: ["1", "2", "3"],
 *       ready: true,
 *       pending: false,
 *       error: false
 *     }
 *   },
 *   entities: {
 *     mexicanFoods: {
 *       "1": {name: "Tacos"},
 *       "2": {name: "Enchiladas"},
 *       "3": {name: "Sopes"}
 *     }
 *   }
 * }
 * ```
 *
 * ## Example:
 *
 * ### Define a duck module for mexican foods:
 *
 * File: ducks/mexicanFoods.js
 * ```
 * import DuckEntity from './DuckEntity'
 *
 * const entity = new DuckEntity('mexicanFoods');
 * const reducer = entity.reducer;
 * export {reducer as default, entity};
 * ```
 *
 * Per convention a redux duck exports a reducer as its default export (this convention is a little arbitrary and
 * we may choose to abandon it). And we also export the `DuckEntity` object as `entity`.
 *
 * ### Initialize duck reducers:
 *
 * ```
 * import mexicanFoods, {entity as mexicanFood} from './ducks/mexicanFoods';
 *
 * const reducers = combineReducers({
 *   entities: combineReducers({
 *     mexicanFoods: mexicanFood.entityReducer,
 *     # ...other "entity" reducers...
 *   }),
 *
 *   mexicanFoods,
 *   # ...other "result" reducers...
 * });
 * ```
 *
 * ### Use the duck:
 *
 * ```
 * import {entity as mexicanFood} from '../ducks/mexicanFoods';
 *
 * # Map all mexican foods into component props
 * const mapStateToProps = state => ({
 *   allMexicanFoods: mexicanFood.all(state)
 * });
 *
 * # Load mexican foods inside a component
 * componentDidMount() {
 *   this.props.dispatch(mexicanFood.actions.load());
 * }
 *
 * # Render mexican foods:
 * <div>
 *   {allMexicanFoods.pending && <FontAwesome name="taco" spin/>}
 *   {allMexicanFoods.ready && <ul>{allMexicanFoods.map(food => <li>{food.name}</li>)}</ul>}
 * </div>
 *
 * # Create tortilla soup
 * dispatch(mexicanFood.actions.create({name: 'Tortilla Soup'}));
 * ```
 *
 * @param name the entity name, camelcase and pluralized (i.e. mexicanFoods)
 * @param {object} options - overrides default options
 * @param {string} options.apiPath - api path passed to `apiFetch` (ex. default: "mexican_foods")
 * @param {string} options.jsonRoot - REST API root wrapper key for a single object (ex. default: "mexican_food")
 * @param {string} options.jsonIndexRoot - REST API root wrapper key for a list of objects (ex. default:
 *   "mexican_foods")
 * @param {object} options.schema - normalizer schema (ex. default: new Schema("mexicanFoods")
 * @param {object} options.loadOptions - options object passed to `ApiFetch` for LOAD action
 * @param {object} options.createOptions - options object passed to `ApiFetch` for CREATE action
 * @param {object} options.updateOptions - options object passed to `ApiFetch` for UPDATE action
 * @param {object} options.deleteOptions - options object passed to `ApiFetch` for DELETE action
 * @param {function} options.collectionApiPath - Function that generates the path to the collection (i.e. '/api/tacos')
 * @param {function} options.entityApiPath - Function that generates the path to a specific entity (i.e.
 *   '/api/tacos/1')
 * @constructor
 */
export default function DuckEntity(nameOrDuck, options = {}) {
  let config = null;
  let name = nameOrDuck;

  if (typeof(nameOrDuck) === 'object' && nameOrDuck.config) {
    config = {...nameOrDuck.config, ...options};
    name = config.name;
  }
  else if (typeof(nameOrDuck) === 'string'){
    const underscored = name.replace(/[A-Z]/g, x => '_' + x.toLowerCase());
    const apiPath = options.apiPath || underscored;
    const defaults = {
      apiPath: underscored,
      jsonRoot: apiPath.endsWith('s') ? apiPath.slice(0, -1) : apiPath,
      jsonIndexRoot: apiPath,
      schema: new Schema(name),
      schemaDefinition: {},
      loadOptions: {},
      createOptions: {},
      updateOptions: {},
      deleteOptions: {},
      collectionApiPath: () => apiPath,
      entityApiPath: (id) => `${apiPath}/${id}`,
      bulkApiPath: () => `${apiPath}/bulk`
    };

    config = {name, ...defaults, ...options};

    if(config.schemaDefinition){
      config.schema.define(config.schemaDefinition);
    }
  }
  else {
    throw "First parameter must be another duck or the name of the duck"
  }

  let types = {
    LOAD_BEGIN: null,
    LOAD_ERROR: null,
    LOAD_SUCCESS: null,
    CACHE_MANY: null,

    GET_BEGIN: null,
    GET_ERROR: null,
    GET_SUCCESS: null,
    CACHE_SINGLE: null,

    SAVE_BEGIN: null,
    SAVE_SUCCESS: null,
    SAVE_ERROR: null,

    DELETE_BEGIN: null,
    DELETE_SUCCESS: null,
    DELETE_ERROR: null,

    REORDER_BEGIN: null,
    REORDER_ERROR: null,
    REORDER_SUCCESS: null,

    MARK_DIRTY: null
  };

  types = actionize(types, (action) => `schooladmin/${name}/${action}`);


  // Initial meta state for a given object or collection
  const initialMetaState = {
    ready: false,
    pending: true,
    error: false,
    errorMessage: undefined,
    fetching: false,
    writing: false
  };

  // Initial state for a given "loadState"
  const initialLoadState = {
    ...initialMetaState,
    results: [],
  };

  /**
   * Returns loaded instances of the entity from state.
   *
   * The returned array also has some properties that reflect the "loading" state of the instances:
   * * {boolean} `ready` - true if the array is loaded
   * * {boolean} `pending` - true if the array has not been loaded yet
   * * {boolean} `error` - true if there was an error loading the data
   *
   * @param {object} state - redux state
   * @param {string} stateKey - state key to pull results from (defaults to "_all")
   * @returns {array} - array of entity objects (empty array if objects are not loaded)
   */
  const all = (state, stateKey = '_all') => {
    let loadState = state[name][stateKey] || initialLoadState;
    const results = denormalize(loadState.results, state.entities, arrayOf(config.schema));

    results.ready = loadState.ready;
    results.pending = loadState.pending;
    results.error = loadState.error;
    results.fetching = loadState.fetching;
    results.writing = loadState.writing;
    results.errorMessage = loadState.errorMessage;
    return results;
  };

  /**
   * Returns a fully denormalized instance of a single entity from state.
   *
   * @param state - redux state
   * @param id - ID of the object you're looking up.
   * @returns {{value: (*|Object), meta: (*|{ready: boolean, pending: boolean, error: boolean})}}
   */
  const single = (state, id) => {
    id = resolveAlias(state[name], id);

    const value = denormalize(id, state.entities, config.schema) || {};
    const meta = state[name]['_meta'][id] || initialMetaState;

    return {value, meta};
  };

  const initialState = {
    "_meta": {},
    "aliases": {}
  };

  // Some actions don't know the ID of the object they're fetching and will use an alias like "current".
  // Once the object is fetched, this maps it to its real ID so subsequent requests will fetch the correct metadata.
  const resolveAlias = (state, alias) => {
    if(state.aliases && state.aliases[alias]){
      return state.aliases[alias];
    }

    return alias
  };

  const resolveListObjectAliases = (list) => {
    const aliases = {};
    list.forEach(id => {
      aliases[id] = id;
    });
    return aliases;
  };

  const resolveListObjectMeta = (list, itemMeta) => {
    const meta = {};
    list.forEach(id => {
      meta[id] = itemMeta;
    });
    return meta;
  };

  /**
   * Updates state with objects returned from an API call via `ApiAction'.
   *
   * The "LOAD" actions handled by this reducer have a `stateKey` value that indicates where to store the
   * results of a load. By default this key is "_all" meaning all entities from the server.
   *
   * If a subset of entities is returned from the server it will be stored under a different `stateKey`.
   *
   * Initial state:
   *
   * {}
   *
   * Action:
   *
   * {
   *   type: "schooladmin/mexicanFoods/LOAD_SUCCESS",
   *   stateKey: "_all",
   *   payload: {
   *     result: {
   *       mexican_foods: ["1", "2"]
   *     }
   *   }
   * }
   *
   * Reduced state:
   *
   * {
   *   _all: {
   *     results: ["1", "2"],
   *     ready: true,
   *     pending: false,
   *     error: false
   *   }
   * }
   *
   * @param {object} state - current redux state
   * @param {object} action - action object from `apiFetch`
   * @returns {*} reduced state
   */
  const reducer = (state = initialState, action) => {
    const {payload = {}, stateKey = '_all'} = action;
    const {result = {}} = payload;
    const loadState = state[stateKey] || initialLoadState;
    const objectId = result[config.jsonRoot] || action.id;
    const objectIds = result[config.jsonIndexRoot] ? result[config.jsonIndexRoot].slice(0) : [];

    // Generic metadata for objects in a successful state.
    const successMeta = {
      ready: true,
      pending: false,
      error: false,
      errorMessage: undefined,
      fetching: false,
      writing: false
    };

    // if response had any flash messages, just show them now
    if (result.meta && result.meta.flash) {
      Utility.displayFlashMessages(result.meta.flash);
    }

    switch (action.type) {
      case types.GET_BEGIN:
        const beginMeta = {
          ready: false,
          pending: true,
          error: false,
          errorMessage: undefined,
          fetching: true
        };

        return {
          ...state,
          _meta: {
            ...state._meta,
            [action.id]: beginMeta,
            [resolveAlias(state, action.id)]: beginMeta
          }
        };

      case types.CACHE_SINGLE:
      case types.GET_SUCCESS:
        return {
          ...state,
          _meta: {
            ...state._meta,
            [objectId]: successMeta,
            [action.id]: successMeta,
            [resolveAlias(state, action.id)]: successMeta
          },
          aliases: {
            ...state.aliases,
            [action.id]: objectId
          }
        };

      case types.GET_ERROR:
        const errorMeta = {
          ready: true,
          pending: false,
          error: true,
          errorMessage: payload.message || payload.error,
          fetching: false
        };

        return {
          ...state,
          _meta: {
            ...state._meta,
            [action.id]: errorMeta,
            [resolveAlias(state, action.id)]: errorMeta
          }
        };

      case types.LOAD_BEGIN:
        return {
          ...state,
          [stateKey]: {
            ...loadState,
            ready: false,
            pending: true,
            error: false,
            errorMessage: undefined,
            fetching: true
          }
        };

      case types.CACHE_MANY:
      case types.LOAD_SUCCESS:
      case types.REORDER_SUCCESS:
        return {
          ...state,
          [stateKey]: {
            ...loadState,
            results: objectIds,
            ready: true,
            pending: false,
            fetching: false,
            errorMessage: result['error']
          },
          _meta: {
            ...state._meta,
            ...resolveListObjectMeta(objectIds, successMeta)
          },
          aliases: {
            ...state.aliases,
            ...resolveListObjectAliases(objectIds)
          }
        };

      case types.SAVE_BEGIN:
        const beginSaveMeta = {
          ...successMeta,
          writing: true
        };

        const meta = { ...state._meta };

        if (objectId) {
          meta[objectId] = beginSaveMeta;
        }

        if (action.id) {
          meta[action.id] = beginSaveMeta;
          meta[resolveAlias(state, action.id)] = beginSaveMeta;
        }

        return {
          ...state,
          [stateKey]: {
            ...loadState,
            writing: true
          },
          _meta: meta
        };

      case types.SAVE_ERROR:
        return {
          ...state,
          [stateKey]: {
            ...loadState,
            writing: false,
            error: true,
            errorMessage: payload.message || payload.error
          }
        };

      case types.MARK_DIRTY:
        const {stateKeys = []} = action;

        let {_meta, ...newState} = state;

        const markDirtyIfNeeded = (inState, key) => {
          if ((inState[key].ready) && (stateKeys.length <= 0 || stateKeys.find((k) => k === key))) {
            return {
              ...inState,
              [key]: {
                ...inState[key],
                ready: false
              }
            };
          }
          else{
            return inState;
          }
        };


        // Mark sets as dirty
        Object.keys(newState).forEach((key) => {
          newState = markDirtyIfNeeded(newState, key);
        });

        // Mark single objects as dirty
        Object.keys(_meta).forEach((key) => {
          _meta = markDirtyIfNeeded(_meta, key);
        });

        return {
          ...newState,
          _meta
        };

      case types.LOAD_ERROR:
        return {
          ...state,
          [stateKey]: {
            ...loadState,
            error: true,
            fetching: false,
            errorMessage: payload.message || payload.error
          }
        };

      case types.SAVE_SUCCESS:
        return {
          ...state,
          [stateKey]: {
            ...loadState,
            writing: false,
            error: false,
            results: action.create ? [
              ...state[stateKey].results,
              result[config.jsonRoot]
            ] : state[stateKey].results
          },
          _meta: {
            ...state._meta,
            [objectId]: successMeta,
            [action.id]: successMeta,
            [resolveAlias(state, action.id)]: successMeta
          },
          aliases: {
            ...state.aliases,
            [action.id]: objectId
          }
        };

      case types.DELETE_SUCCESS:
        const deletedId = result[config.jsonRoot];
        return deletedId && state[stateKey]
          ? {...state, [stateKey]: {...loadState, results: state[stateKey].results.filter(id => id !== deletedId)}}
          : state;

      default:
        return state;
    }
  };

  /**
   * Action creator to load objects.
   *
   * By default this method returns all objects returned by the API index action. Query parameters can be specified
   * in which case `stateKey` must also be specified to indicate where the results should be stored in state. The
   * `stateKey` value can be passed to the `all` method to get the desired result set.
   *
   * @param {object} query - query parameters to pass through (defaults to no parameters)
   * @param {string} stateKey - key under which to store results (defaults to "_all")
   */
  const load = (query = {}, stateKey = '_all') =>  {
    if (Object.keys(query).length !== 0 && stateKey === '_all') {
      throw new Error("stateKey must be specified when using a query");
    }

    // Combine query with config.loadOptions.query
    let combinedQuery = [];

    if (config.loadOptions.query) {
      combinedQuery.push(config.loadOptions.query);
    }

    if (Object.keys(query).length !== 0) {
      combinedQuery.push(stringParams(query));
    }

    combinedQuery = combinedQuery.join('&');

    return apiAction(
      types.LOAD_BEGIN, types.LOAD_SUCCESS, types.LOAD_ERROR,
      (state) => apiFetch.get(state, config.collectionApiPath(), {...config.loadOptions, query: combinedQuery}),
      {
        entityName: name,
        stateKey: stateKey,
        query: combinedQuery
      },
      {[config.jsonIndexRoot]: arrayOf(config.schema)});
  };

  /**
   * Action to cache a set of objects, with an associated stateKey. i.e.
   *
   * dispatch(duck.cacheMany([
   *     {id: 'abc123', name: 'daffy'},
   *     {id: 'def456', name: 'darkwing'}
   * ]);
   *
   * duck.all();
   *
   * @param entities
   * @param stateKey
   * @returns {{type: *, entityName: *, stateKey: string, payload: NormalizeOutput}}
   */
  const cacheMany = (entities, stateKey = '_all') => {
    return {
      type: types.CACHE_MANY,
      entityName: name,
      stateKey,
      payload: normalize(
        {[config.jsonIndexRoot]: entities},
        {[config.jsonIndexRoot]: arrayOf(config.schema)}
      )
    }
  };

  /**
   * Action to cache a single object, optionally by an alias.  i.e.
   *
   * dispatch(duck.cacheSingle({
   *    name: 'daffy',
   *    id: 'abc123'
   * ), 'best_taco'}
   *
   * duck.single('abc123'); // get by ID
   * duck.single('best_taco'); //get by alias
   *
   * @param entity
   * @param alias
   */
  const cacheSingle = (entity, alias) => {
    return {
      type: types.CACHE_SINGLE,
      entityName: name,
      id: alias || entity.id,
      payload: normalize(
        {[config.jsonRoot]: entity},
        {[config.jsonRoot]: config.schema}
      )
    }
  };

  /**
   * Action to mark all lists dirty so clients will know to re-fetch them from the UI.
   *
   * @param stateKeys
   */
  const markDirty = (...stateKeys) => (dispatch) => {
    dispatch({
      type: types.MARK_DIRTY,
      stateKeys
    });

    return Promise.resolve();
  };

  /**
   * Updates object ordering
   *
   * The API controller must have an "update_ordering" action.
   *
   * @param ordering - array of entity IDs reflecting the new ordering
   * @param stateKey
   */
  const reorder = (ordering, stateKey = '_all') => {
    return (dispatch) => {
      // For UI responsiveness, update ordering locally before sending API request
      dispatch({
        type: types.CACHE_MANY,
        stateKey,
        payload: {
          result: {
            [config.jsonIndexRoot]: ordering
          }
        }
      });

      return dispatch(
        apiAction(
          types.REORDER_BEGIN, types.REORDER_SUCCESS, types.REORDER_ERROR,
          (state) => apiFetch.post(state, `${config.collectionApiPath()}/update_ordering`, {ordering}),
          {entityName: name, stateKey},
          {[config.jsonRoot]: config.schema}
        )
      );
    };
  };

  /**
   * Action creator to load a single object
   */
  const get = (id) => apiAction(
    types.GET_BEGIN, types.GET_SUCCESS, types.GET_ERROR,
    (state) => apiFetch.get(state, config.entityApiPath(id), config.loadOptions),
    {
      entityName: name,
      id: id
    },
    {[config.jsonRoot]: config.schema}
  );

  /**
   * Action creator to create an object.
   */
  const create = (object, params = {}, stateKey = '_all') => apiAction(
    types.SAVE_BEGIN, types.SAVE_SUCCESS, types.SAVE_ERROR,
    (state) => apiFetch.post(state, config.collectionApiPath(), { [config.jsonRoot]: object, ...params }, config.createOptions),
    {
      entityName: name,
      create: true,
      update: false,
      stateKey
    },
    { [config.jsonRoot]: config.schema }
  );

  /**
   * Action creator to update an object.
   */
  const update = (object, params = {}, stateKey = '_all') =>
    apiAction(
    types.SAVE_BEGIN, types.SAVE_SUCCESS, types.SAVE_ERROR,
    (state) => apiFetch.put(state, config.entityApiPath(object.id), { [config.jsonRoot]: object, ...params }, config.updateOptions),
    {
      entityName: name,
      create: false,
      update: true,
      id: object.id,
      stateKey
    },
    { [config.jsonRoot]: config.schema }
  );

  /**
   * Action creator to update many objects.
   */
  const bulkUpdate = (objects, params = {}, stateKey = '_all') => apiAction(
    types.SAVE_BEGIN, types.SAVE_SUCCESS, types.SAVE_ERROR,
    (state) => apiFetch.put(state, config.bulkApiPath(), { [config.jsonIndexRoot]: objects, ...params }, config.updateOptions),
    {
      entityName: name,
      create: false,
      update: true,
      stateKey
    },
    { [config.jsonRoot]: config.schema }
  );

  /**
   * Action creator to create or update an object.
   */
  const save = (object, params = {}, stateKey = '_all') => (
    object.id
      ? update(object, params, stateKey)
      : create(object, params, stateKey)
  );

  /**
   * Action creator to delete an object.
   */
  const remove = (object, stateKey = '_all') => apiAction(
    types.DELETE_BEGIN, types.DELETE_SUCCESS, types.DELETE_ERROR,
    (state) => apiFetch.delete(state, config.entityApiPath(object.id), config.deleteOptions),
    {
      entityName: name,
      object,
      stateKey
    },
    { [config.jsonRoot]: config.schema }
  );

  /**
   * Action creator to delete many objects.
   */
  const bulkRemove = (idsToDelete) => apiAction(
    types.DELETE_BEGIN, types.DELETE_SUCCESS, types.DELETE_ERROR,
    (state) => apiFetch.bulkDelete(state, config.bulkApiPath(), {[config.jsonRoot]: idsToDelete}, config.deleteOptions),
    {
      entityName: name,
      idsToDelete,
    },
    {[config.jsonRoot]: config.schema}
  );


  /**
   * Takes a response from another action and returns the denormalized result from that action.
   *
   * dispatch(actions.create({
   *   food: "taco",
   *   tortilla: "corn"
   * }).then(denormalizeObjects)
   *
   * returns: {
   *   id: "abc123",
   *   food: "taco",
   *   tortilla: "corn"
   * }
   *
   * dispatch(actions.load({tortilla: 'corn'}).then(denormalizeObjects)
   *
   * returns: [
   *   {id: "abc123", food: "taco", tortilla: "corn"},
   *   {id: "def456", food: "burrito", tortilla: "flour"}
   *   // etc...
   * ]
   *
   * @param response - pass through response from previous action
   * @returns {*|Object|Immutable.Map|array|Immutable.list}
   */
  const denormalizeObjects = (response) =>{
    if(response && response.result){
      if(response.result[config.jsonRoot]){
        return Promise.resolve(
          denormalize(response.result[config.jsonRoot], response.entities, config.schema)
        );
      }
      else if(response.result[config.jsonIndexRoot]){
        return Promise.resolve(
          denormalize(response.result[config.jsonIndexRoot], response.entities, arrayOf(config.schema))
        );
      }
    }

    return Promise.resolve(response);
  };

  // Properties
  this.name = name;
  this.config = config;
  this.actions = {
    ...types,
    cacheMany,
    cacheSingle,
    create,
    denormalizeObjects,
    get,
    load,
    markDirty,
    remove,
    bulkRemove,
    reorder,
    save,
    update,
    bulkUpdate
  };
  this.schema = config.schema;
  this.types = types;
  this.initialState = initialState;

  // TODO : make generic hooks like `useSingle` etc...
  this.hooks = {};

  // Functions
  this.all = all;
  this.single = single;
  this.reducer = reducer;
  this.entityReducer = entityReducer(this.name);
};
